import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { trpc } from "../../lib/trpc.js";
import { Pagina } from "../../components/Pagina.js";
import { Area, Botao, Campo, Entrada, Falha } from "../../components/ui.js";
import { GradeSelecao } from "../geracao/EtapaBiblioteca.js";
import { EnviarFotos, type FotoDoLote } from "./EnviarFotos.js";
import { EscolherEvento } from "./EscolherEvento.js";

/**
 * Montar um lote.
 *
 * A ordem dos blocos é a da cabeça do operador quando ele volta de um evento: o
 * que é isto, quais fotos, quem entra nelas, e o que pedir. O saldo aparece só
 * no fim, ao lado do botão, porque é informação para decidir se cria agora — não
 * para escolher as fotos.
 */
export function NovoLotePage() {
  const navegar = useNavigate();

  const [nome, setNome] = useState("");
  const [fotos, setFotos] = useState<FotoDoLote[]>([]);
  const [personagemIds, setPersonagemIds] = useState<number[]>([]);
  const [personagemNome, setPersonagemNome] = useState("");
  const [personagemDescricao, setPersonagemDescricao] = useState("");
  const [pedidoExtra, setPedidoExtra] = useState("");
  const [institutionId, setInstitutionId] = useState("");
  const [vivenciaId, setVivenciaId] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const banco = trpc.imageBank.list.useQuery();
  const saldo = trpc.lotes.saldo.useQuery();

  const criar = trpc.lotes.criar.useMutation({ onError: (e) => setErro(e.message) });

  const itensDoBanco = banco.data ?? [];
  const restantes = saldo.data?.restantes ?? null;
  const naoCabem = restantes === null ? 0 : Math.max(0, fotos.length - restantes);

  // Sem nome não há como o prompt citar o personagem, e é o servidor que recusa.
  const faltaNomeDoPersonagem = personagemIds.length > 0 && personagemNome.trim() === "";
  const podeCriar =
    nome.trim() !== "" && fotos.length > 0 && vivenciaId !== "" && !faltaNomeDoPersonagem;

  function alternarReferencia(id: number) {
    setPersonagemIds((atual) => {
      if (atual.includes(id)) return atual.filter((x) => x !== id);
      // Quatro é o limite de colunas auxiliares da geração.
      if (atual.length >= 4) return atual;
      return [...atual, id];
    });
  }

  return (
    <Pagina
      titulo="Novo lote"
      descricao="Cada foto é analisada, ganha um prompt escrito a partir dela, e vai para a fila de geração."
    >
      <div className="mx-auto flex max-w-lg flex-col gap-7">
        {erro && <Falha mensagem={erro} tentarNovamente={() => setErro(null)} />}

        <Campo rotulo="Nome do lote" dica="Como você vai reconhecê-lo depois.">
          <Entrada
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="MEPE-SP 20/09"
            maxLength={120}
          />
        </Campo>

        <EscolherEvento
          institutionId={institutionId}
          vivenciaId={vivenciaId}
          aoMudarLocal={setInstitutionId}
          aoMudarVivencia={setVivenciaId}
        />

        <section className="flex flex-col gap-3">
          <div>
            <h2 className="font-display text-[1.125rem] leading-tight">As fotos</h2>
            <p className="mt-1 text-[0.8125rem] leading-relaxed text-texto-suave">
              Escolha todas de uma vez. Cada uma sobe por conta própria, então uma que falhe não
              leva as outras junto.
            </p>
          </div>
          <EnviarFotos fotos={fotos} aoMudar={setFotos} />
        </section>

        <section className="flex flex-col gap-3">
          <div>
            <h2 className="font-display text-[1.125rem] leading-tight">
              O personagem <span className="text-[0.875rem] text-texto-fraco">— opcional</span>
            </h2>
            <p className="mt-1 text-[0.8125rem] leading-relaxed text-texto-suave">
              Até quatro fotos do banco de imagens. Mais de uma ajuda: o modelo descreve o
              personagem uma vez, a partir de todas, e usa essa descrição em todas as fotos.
            </p>
          </div>

          <GradeSelecao
            itens={itensDoBanco.map((i) => ({ id: i.id, url: i.url, label: i.label }))}
            selecionados={personagemIds}
            aoAlternar={alternarReferencia}
            carregando={banco.isLoading}
            vazio={{
              titulo: "O banco de imagens está vazio",
              descricao: "Suba as fotos do personagem em Acervo → Banco de imagens.",
            }}
          />

          {personagemIds.length > 0 && (
            <>
              <Campo
                rotulo="Nome do personagem"
                erro={faltaNomeDoPersonagem ? "O prompt precisa citá-lo pelo nome" : null}
              >
                <Entrada
                  value={personagemNome}
                  onChange={(e) => setPersonagemNome(e.target.value)}
                  placeholder="Dr. Bezerra de Menezes"
                  maxLength={120}
                />
              </Campo>

              <Campo
                rotulo="O que mais se sabe sobre ele"
                dica="Opcional. A aparência é lida das fotos; isto é o que elas não mostram."
              >
                <Area
                  value={personagemDescricao}
                  onChange={(e) => setPersonagemDescricao(e.target.value)}
                  maxLength={2000}
                />
              </Campo>
            </>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <div>
            <h2 className="font-display text-[1.125rem] leading-tight">
              O pedido <span className="text-[0.875rem] text-texto-fraco">— opcional</span>
            </h2>
            <p className="mt-1 text-[0.8125rem] leading-relaxed text-texto-suave">
              O enquadramento, a iluminação, a naturalidade e a preservação de quem já está na foto
              o sistema já exige sozinho. Escreva aqui só o que ele não teria como saber.
            </p>
          </div>

          <Campo rotulo="Pedido extra" dica="Vale para todas as fotos do lote.">
            <Area
              value={pedidoExtra}
              onChange={(e) => setPedidoExtra(e.target.value)}
              placeholder={
                "o personagem deve estar sorrindo\n" +
                "o personagem está autografando um livro\n" +
                "o personagem deve abraçar paternalmente"
              }
              maxLength={2000}
            />
          </Campo>
        </section>

        <section className="flex flex-col gap-3 border-t border-borda pt-5">
          {restantes !== null && (
            <p className="text-[0.8125rem] leading-relaxed text-texto-suave">
              Seu pacote tem <span className="num font-medium text-texto">{restantes}</span>{" "}
              {restantes === 1 ? "foto" : "fotos"}.
              {naoCabem > 0 && (
                <>
                  {" "}
                  <span className="text-texto">
                    {naoCabem} {naoCabem === 1 ? "foto ficará" : "fotos ficarão"} aguardando saldo
                  </span>{" "}
                  — elas não são analisadas nem geradas até você retomar o lote.
                </>
              )}
            </p>
          )}

          <Botao
            tom="brasa"
            largo
            disabled={!podeCriar}
            carregando={criar.isPending}
            onClick={() => {
              setErro(null);
              criar.mutate(
                {
                  nome: nome.trim(),
                  fotos: fotos.map((f) => ({ path: f.path, nomeOriginal: f.nomeOriginal })),
                  personagemIds,
                  personagemNome: personagemNome.trim(),
                  personagemDescricao: personagemDescricao.trim(),
                  pedidoExtra: pedidoExtra.trim(),
                  vivenciaId,
                },
                { onSuccess: (r) => navegar(`/lotes/${r.loteId}`) },
              );
            }}
          >
            {fotos.length === 0
              ? "Escolha as fotos"
              : vivenciaId === ""
                ? "Escolha o local e a vivência"
                : `Criar lote com ${fotos.length} ${fotos.length === 1 ? "foto" : "fotos"}`}
          </Botao>
        </section>
      </div>
    </Pagina>
  );
}
